"use client";
import { useState } from "react";
import {
  Calendar,
  MapPin,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Globe,
  GraduationCap,
  Mail,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
type FilterKey = "all" | "events" | "training" | "news";
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "events", label: "Conferences & Events" },
  { key: "training", label: "IICRC Training" },
  { key: "news", label: "News & Standards" },
];
type HardcodedEvent = {
  id: string;
  category: string;
  categoryLabel: string;
  title: string;
  organizer: string;
  dates: string;
  startDate: string;
  location: string;
  description: string;
  link: string;
  badgeClass: string;
  featured: boolean;
  verified: boolean;
};
type NewsItem = {
  id: string;
  tag: string;
  tagClass: string;
  headline: string;
  body: string;
  source: string;
  link: string;
};
type IicrcCert = { name: string; code: string; who: string };
type Org = { name: string; type: string; desc: string; link: string };
function countdownBadge(startDate: string): { label: string; cls: string } {
  const days = Math.ceil(
    (new Date(startDate).getTime() - Date.now()) / 86_400_000,
  );
  if (days < 0) return { label: "PAST", cls: "bg-zinc-200 text-zinc-500" };
  if (days <= 30) return { label: `${days} DAYS AWAY`, cls: "bg-red-600 text-white" };
  if (days <= 90) return { label: "UPCOMING", cls: "bg-amber-100 text-[#B88A2F]" };
  return { label: "SAVE THE DATE", cls: "bg-zinc-100 text-zinc-600" };
}
export function IndustryNewsClient({
  events,
  news,
  iicrcCerts,
  orgs,
}: {
  events: HardcodedEvent[];
  news: NewsItem[];
  iicrcCerts: IicrcCert[];
  orgs: Org[];
}) {
  const [filter, setFilter] = useState<FilterKey>("all");
  const showEvents = filter === "all" || filter === "events";
  const showTraining = filter === "all" || filter === "training";
  const showNews = filter === "all" || filter === "news";
  return (
    <>
      {/* FILTER TABS */}
      <section style={{ padding: "32px 24px", background: "#080F1A" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={
                  filter === f.key
                    ? "rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white"
                    : "rounded-full border border-white/20 bg-transparent px-4 py-2 text-sm font-semibold transition-colors hover:border-zinc-500"
                }
                style={filter !== f.key ? { color: "rgba(255,255,255,0.82)" } : undefined}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* EVENTS */}
      {showEvents && (
        <section style={{ padding: "80px 24px", background: "#080F1A" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", background: "rgba(5,13,24,0.78)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.18)", boxShadow: "0 4px 32px rgba(0,0,0,0.5)", padding: "56px 48px" }}>
            <div className="h-[2px] w-12 bg-[#D4A84B]" />
            <p className="mt-3 text-sm font-semibold uppercase tracking-widest" style={{ color: "#D4A84B" }}>
              2026 Conference Calendar
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl" style={{ color: "#ffffff", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Upcoming Events
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {events.map((e) => {
                const cd = countdownBadge(e.startDate);
                return (
                  <article
                    key={e.id}
                    className={cn(
                      "card-hover rounded-2xl border border-white/15 bg-transparent p-6",
                      e.featured && "md:col-span-2 border-l-4 border-l-red-600",
                    )}
                    style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn("rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest", e.badgeClass)}>
                        {e.categoryLabel}
                      </span>
                      <span className={cn("rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest", cd.cls)}>
                        {cd.label}
                      </span>
                    </div>
                    <h3 className="mt-4 text-xl font-bold" style={{ color: "#ffffff" }}>{e.title}</h3>
                    <p className="mt-1 text-sm font-semibold" style={{ color: "#D4A84B" }}>{e.organizer}</p>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm" style={{ color: "rgba(255,255,255,0.82)" }}>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="size-4" /> {e.dates}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="size-4" /> {e.location}
                      </span>
                    </div>
                    <p className="mt-4 text-sm" style={{ color: "rgba(255,255,255,0.82)" }}>{e.description}</p>
                    {!e.verified && (
                      <p className="mt-3 flex items-center gap-2 text-xs" style={{ color: "#D4A84B" }}>
                        <AlertTriangle className="size-3.5" /> Date unconfirmed — verify at organizer website
                      </p>
                    )}
                    <a
                      href={e.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                      style={{ color: "#D4A84B" }}
                    >
                      Visit official site <ExternalLink className="size-3.5" />
                    </a>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}
      {/* IICRC CERTIFICATION TABLE */}
      {showTraining && (
        <section style={{ padding: "80px 24px", background: "#080F1A" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", background: "rgba(5,13,24,0.78)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.18)", boxShadow: "0 4px 32px rgba(0,0,0,0.5)", padding: "56px 48px" }}>
            <div className="h-[2px] w-12 bg-[#D4A84B]" />
            <p className="mt-3 text-sm font-semibold uppercase tracking-widest" style={{ color: "#D4A84B" }}>
              IICRC Certification
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl" style={{ color: "#ffffff", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              The Industry Baseline
            </h2>
            <p className="mt-4 max-w-3xl text-lg" style={{ color: "rgba(255,255,255,0.82)", lineHeight: "1.75" }}>
              IICRC certification is not optional for serious restoration
              contractors — it is the minimum standard expected by TPAs,
              carriers, healthcare facility managers, and BOMA/IFMA property
              managers. DRR technicians hold active certifications across all
              core disciplines.
            </p>
            <div className="mt-12 space-y-3">
              <div className="hidden rounded-xl bg-transparent px-6 py-3 text-[11px] font-semibold uppercase tracking-widest md:grid md:grid-cols-4" style={{ color: "#777" }}>
                <span>Certification</span>
                <span>Credential</span>
                <span>Who Needs It</span>
                <span className="text-right">Register</span>
              </div>
              {iicrcCerts.map((c) => (
                <div
                  key={c.code}
                  className="card-hover grid gap-2 rounded-xl border border-white/15 bg-transparent px-6 py-4 md:grid-cols-4 md:items-center md:gap-0"
                  style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
                >
                  <span className="font-semibold" style={{ color: "#ffffff" }}>{c.name}</span>
                  <span>
                    <span className="inline-flex rounded-md bg-transparent px-2 py-0.5 text-xs font-bold" style={{ color: "#D4A84B" }}>
                      {c.code}
                    </span>
                  </span>
                  <span className="text-sm" style={{ color: "rgba(255,255,255,0.82)" }}>{c.who}</span>
                  <span className="md:text-right">
                    <a
                      href="https://iicrc.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
                      style={{ color: "#D4A84B" }}
                    >
                      Register <ExternalLink className="size-3" />
                    </a>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-start gap-3 rounded-xl border border-red-200 bg-transparent px-6 py-4">
              <AlertTriangle className="mt-0.5 size-5 shrink-0" style={{ color: "#D4A84B" }} />
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.82)" }}>
                IICRC course schedules change frequently. Always verify current
                availability at{" "}
                <a
                  href="https://iicrc.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline underline-offset-2"
                  style={{ color: "#D4A84B" }}
                >
                  iicrc.org
                </a>
                . DRR recommends scheduling training at least 60 days in advance.
              </p>
            </div>
          </div>
        </section>
      )}
      {/* NEWS & STANDARDS */}
      {showNews && (
        <section style={{ padding: "80px 24px", background: "#080F1A" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", background: "rgba(5,13,24,0.78)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.18)", boxShadow: "0 4px 32px rgba(0,0,0,0.5)", padding: "56px 48px" }}>
            <div className="h-[2px] w-12 bg-[#D4A84B]" />
            <p className="mt-3 text-sm font-semibold uppercase tracking-widest" style={{ color: "#D4A84B" }}>
              Standards Watch &amp; Industry Intelligence
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl" style={{ color: "#ffffff", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              What every restoration professional should be tracking right now
            </h2>
            <div className="mt-12 space-y-6">
              {news.map((n) => (
                <article
                  key={n.id}
                  className="card-hover rounded-2xl border border-white/15 bg-transparent p-6"
                  style={{ borderLeft: "3px solid #D4A84B", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
                >
                  <span className={cn("inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest", n.tagClass)}>
                    {n.tag}
                  </span>
                  <h3 className="mt-4 text-xl font-bold" style={{ color: "#ffffff" }}>{n.headline}</h3>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>{n.body}</p>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs" style={{ color: "#777" }}>Source: {n.source}</span>
                    <a
                      href={n.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                      style={{ color: "#D4A84B" }}
                    >
                      Read more <ExternalLink className="size-3.5" />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* ORGANIZATIONS */}
      {(filter === "all" || filter === "events") && (
        <section style={{ padding: "80px 24px", background: "#080F1A" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", background: "rgba(5,13,24,0.78)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.18)", boxShadow: "0 4px 32px rgba(0,0,0,0.5)", padding: "56px 48px" }}>
            <div className="h-[2px] w-12 bg-[#D4A84B]" />
            <p className="mt-3 text-sm font-semibold uppercase tracking-widest" style={{ color: "#D4A84B" }}>
              The Restoration Industry Ecosystem
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl" style={{ color: "#ffffff", fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              The organizations that set standards and govern certifications
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {orgs.map((o) => (
                <div
                  key={o.name}
                  className="card-hover rounded-2xl border border-white/15 bg-transparent p-6"
                  style={{ borderLeft: "3px solid #D4A84B", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
                >
                  <Globe className="size-6" style={{ color: "#D4A84B" }} />
                  <h3 className="mt-4 text-lg font-bold" style={{ color: "#ffffff" }}>{o.name}</h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wider" style={{ color: "#777" }}>
                    {o.type}
                  </p>
                  <p className="mt-3 text-sm" style={{ color: "rgba(255,255,255,0.82)" }}>{o.desc}</p>
                  <a
                    href={`https://${o.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                    style={{ color: "#D4A84B" }}
                  >
                    {o.link} <ExternalLink className="size-3.5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* NEWSLETTER SIGNUP */}
      <section style={{ background: "#DC2626", padding: "80px 24px" }}>
        <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: "clamp(28px, 3.5vw, 40px)", fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.1 }}>
            Never miss a standards update
          </h2>
          <p style={{ fontSize: "17px", color: "rgba(255,255,255,0.85)", lineHeight: 1.7, marginTop: "16px" }}>
            We track IICRC standard updates, TPA network changes, conference
            schedules, and regulatory shifts so you don&rsquo;t have to.
          </p>
          <NewsletterForm />
        </div>
      </section>
    </>
  );
}
function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await fetch("/api/dispatch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Newsletter Signup",
          email: email.trim(),
          phone: "N/A",
          address: "N/A",
          lossType: "other",
          message: "Newsletter signup from Industry News page",
          website: "",
        }),
      });
      setSent(true);
    } catch {
      // silent fail
    } finally {
      setBusy(false);
    }
  }
  if (sent) {
    return (
      <div className="mt-8 flex items-center justify-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.9)" }}>
        <CheckCircle2 className="size-4" /> You&rsquo;re on the list. We&rsquo;ll
        notify you when something important changes.
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mx-auto mt-8 flex max-w-md gap-2">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        className="h-12 flex-1 rounded-lg border border-white/30 bg-transparent/10 px-4 text-sm text-white placeholder:text-white/50 outline-none focus:border-white/60"
        disabled={busy}
      />
      <button
        type="submit"
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-lg bg-transparent px-6 py-3 text-sm font-semibold transition-colors hover:bg-zinc-100 disabled:opacity-50"
        style={{ color: "#D4A84B" }}
      >
        <Mail className="size-4" /> Notify Me
      </button>
    </form>
  );
}
